import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ChevronRight } from "lucide-react";
import { useApi } from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";
import { gbp } from "../lib/format";
import { ErrorState, Loading, PageHeader, VatNote } from "../components/common";
import { KpiCard } from "../components/KpiCard";
import { ProjectsTable } from "../components/ProjectsTable";
import { PartnerMaster } from "../components/PartnerMaster";
import { CompanyPanel } from "../components/Structure";
import { WelcomePackButtons } from "../components/WelcomePack";

const slug = (s) => s.replace(/[^a-z0-9]+/gi, "-").toLowerCase();

export function PartnersList() {
  const { data, error, status, loading } = useApi("/partners");
  if (loading && !data) return <Loading />;
  if (error && !data) return <ErrorState message={error} status={status} />;
  const th = "px-4 py-2.5 text-xs font-semibold text-muted-foreground whitespace-nowrap";
  return (
    <div data-testid="partners-page">
      <PageHeader eyebrow="JV partners" title="Partners" subtitle={`${data.filter((p) => !p.is_internal).length} JV partners plus EPIL's own projects`} />
      <div className="overflow-x-auto rounded-lg border bg-card grid-scroll">
        <table className="dt w-full text-sm" data-testid="partners-table">
          <thead><tr className="border-b text-left">
            <th className={th}>Partner</th><th className={`${th} text-right`}>Projects</th><th className={`${th} text-right`}>Live</th>
            <th className={`${th} text-right`}>Equity requirement</th><th className={`${th} text-right`}>Equity deployed</th><th className={`${th} text-right`}>Equity to deploy</th>
            <th className={`${th} text-right`}>Equity return</th><th className={`${th} text-right`}>Profit</th><th className={th}></th>
          </tr></thead>
          <tbody>
            {data.map((p) => (
              <tr key={p.id} className="border-b last:border-0 hover:bg-accent/60 transition-colors">
                <td className="px-4 py-3 font-semibold"><Link to={`/partners/${p.id}`} className="hover:text-primary" data-testid={`partner-link-${slug(p.name)}`}>{p.name}</Link></td>
                <td className="px-4 py-3 text-right font-figure" data-testid={`partner-count-${slug(p.name)}`}>{p.totals.count}</td>
                <td className="px-4 py-3 text-right font-figure">{p.totals.live}</td>
                <td className="px-4 py-3 text-right font-figure">{gbp(p.totals.proforma_equity)}</td>
                <td className="px-4 py-3 text-right font-figure">{gbp(p.totals.deployed)}</td>
                <td className="px-4 py-3 text-right font-figure">{gbp(p.totals.to_deploy)}</td>
                <td className="px-4 py-3 text-right font-figure">{gbp(p.totals.equity_return)}</td>
                <td className="px-4 py-3 text-right font-figure">{gbp(p.totals.profit)}</td>
                <td className="px-2"><Link to={`/partners/${p.id}`}><ChevronRight className="h-4 w-4 text-muted-foreground" /></Link></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function PartnerView() {
  const { id } = useParams();
  const { isPartner } = useAuth();
  const { data, error, status, loading } = useApi(`/partners/${id}`);
  if (loading && !data) return <Loading />;
  if (error && !data) return <ErrorState message={error} status={status} />;
  const t = data.totals;
  return (
    <div className="space-y-6" data-testid="partner-view-page">
      {!isPartner && <Link to="/partners" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-partners"><ArrowLeft className="h-4 w-4" /> Partners</Link>}
      <PageHeader eyebrow="Partner view" title={<span data-testid="partner-view-name">{data.name}</span>}
        subtitle={data.legal_names?.length ? data.legal_names.join(" · ") : undefined} actions={<WelcomePackButtons partnerId={id} />} />
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KpiCard testid="pv-projects" label="Projects" value={t.count} display={`${t.count}`} hint={`${t.live} live`} />
        <KpiCard testid="pv-proforma-equity" label="Equity requirement" value={t.proforma_equity} />
        <KpiCard testid="pv-deployed" label="Equity deployed" value={t.deployed} />
        <KpiCard testid="pv-to-deploy" label="Equity to deploy" value={t.to_deploy} />
        <KpiCard testid="pv-profit" label="Forecast profit (cashflow)" value={t.profit} tone="pos" />
      </div>
      <VatNote testid="partner-view-vat-note" />
      <ProjectsTable rows={data.projects} totals={t} showPartner={false} />
      <PartnerMaster id={id} />
      <CompanyPanel kind="partners" id={id} title="Top Co – the company that signs the framework agreement" />
    </div>
  );
}
